import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import DateInput from '../components/DateInput';
import './ContratCorpo.css';

const PUBLIC_FORM_URL = process.env.REACT_APP_PUBLIC_FORM_URL || `http://${window.location.hostname}:3002`;

const STATUT_LABELS = {
  brouillon: 'Brouillon', en_construction: 'En construction', envoye: 'Envoyé',
  en_revision: 'En révision', accepte: 'Accepté', refuse: 'Refusé',
  annule: 'Annulé', complete: 'Complété',
};
const BADGE_CLASSES = {
  brouillon: 'badge badge--muted', en_construction: 'badge badge--warning', envoye: 'badge badge--info',
  en_revision: 'badge badge--warning', accepte: 'badge badge--success', refuse: 'badge badge--danger',
  annule: 'badge badge--muted', complete: 'badge badge--gold',
};

const MOIS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'T12:00:00');
  return `${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
}
function fmtMontant(n) { return n != null ? `${Number(n).toFixed(2)} $` : '—'; }
function fmtDatetime(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' });
}

const ONGLETS = ['Soumission', 'Menu', 'Client', 'Commentaires', 'Paiements', 'Documents'];

export default function ContratCorpo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contrat, setContrat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur,  setErreur]  = useState('');
  const [onglet,  setOnglet]  = useState('Soumission');
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState('');

  // Formulaire soumission
  const [formSoum, setFormSoum] = useState({});
  const [acompteOverride, setAcompteOverride] = useState(false);

  // Modal item
  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [formItem, setFormItem] = useState({
    categorie: '', description: '', detail: '', quantite: 1,
    unite: 'personne', prix_unitaire: '', est_rabais: false, visible_menu: true, ordre: 0,
  });

  // Commentaires
  const [nouveauMsg, setNouveauMsg] = useState('');

  // ─── Menus corporatifs ──────────────────────────────────────────────────────
  const [menus,           setMenus]           = useState([]);
  const [menuActif,       setMenuActif]       = useState(null);   // menu sélectionné pour l'édition
  const [menuSections,    setMenuSections]    = useState([]);
  const [showMenuModal,   setShowMenuModal]   = useState(false);
  const [editMenu,        setEditMenu]        = useState(null);
  const [formMenu,        setFormMenu]        = useState({ nom: 'Menu principal', format: 'plateau', description: '', nb_personnes: '', statut: 'brouillon', notes: '', prix_par_personne: 0, nb_personnes_override: '', detail_visible: 0 });
  const [showSectionModal,setShowSectionModal]= useState(false);
  const [editSection,     setEditSection]     = useState(null);
  const [formSection,     setFormSection]     = useState({ nom: '', type: 'fixe', emoji: '🍖', description: '' });
  const [catalogue,       setCatalogue]       = useState([]);
  const [showCatModal,    setShowCatModal]    = useState(false);
  const [catSearch,       setCatSearch]       = useState('');
  const [catCateg,        setCatCateg]        = useState('');
  const [sectionCible,    setSectionCible]    = useState(null);  // section où ajouter un item
  const [loadingMenus,    setLoadingMenus]    = useState(false);
  const [confirmPrixModal,setConfirmPrixModal]= useState(null); // { warning, message, ancien_total, nouveau_total, difference, menuId }
  const [expandedMenuItems,setExpandedMenuItems] = useState(new Set()); // item IDs expanded in soumission
  const [menuDetailsCache, setMenuDetailsCache]  = useState({});        // { itemId: sections[] }

  // ─── Menus préenregistrés (prix_catalogue) ─────────────────────────────────
  const [menusCatalogue,       setMenusCatalogue]       = useState([]);
  const [showMenuCatDropdown,  setShowMenuCatDropdown]  = useState(false);
  const [appliqueMenuLoading,  setAppliqueMenuLoading]  = useState(false);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/corpo/contrats/${id}`);
      setContrat(data);
      setFormSoum({
        date_evenement: data.date_evenement || '',
        nb_personnes:   data.nb_personnes   || 50,
        lieu:           data.lieu           || '',
        adresse:        data.adresse        || '',
        heure_arrivee:  data.heure_arrivee  || '',
        heure_service:  data.heure_service  || '',
        heure_depart:   data.heure_depart   || '',
        acompte_pct:    data.acompte_pct    || 40,
        acompte_montant:data.acompte_montant|| '',
        tps_pct:        data.tps_pct        || 5,
        tvq_pct:        data.tvq_pct        || 9.975,
        notes_internes: data.notes_internes || '',
        notes_client:   data.notes_client   || '',
      });
      setAcompteOverride(data.acompte_montant !== null && data.acompte_montant !== undefined);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  // Marquer commentaires lus quand on ouvre l'onglet
  useEffect(() => {
    if (onglet === 'Commentaires' && contrat) {
      api.patch(`/corpo/contrats/${id}/commentaires/lu`).catch(() => {});
    }
  }, [onglet, id, contrat]);

  // Charger menus quand on ouvre l'onglet Menu ou Soumission
  useEffect(() => {
    if ((onglet === 'Menu' || onglet === 'Soumission') && contrat) {
      chargerMenus();
      if (!catalogue.length && onglet === 'Menu') {
        api.get('/catalogue?actif=1').then(d => setCatalogue(d || [])).catch(() => {});
      }
    }
  }, [onglet, contrat]); // eslint-disable-line

  // Charger menus préenregistrés du catalogue de prix
  useEffect(() => {
    if (onglet === 'Soumission' && menusCatalogue.length === 0) {
      api.get('/menus-corpo?actif=1').then(d => setMenusCatalogue(d || [])).catch(() => {});
    }
  }, [onglet]); // eslint-disable-line

  async function ouvrirPdf(path) {
    try {
      const blob = await api.blob(path);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      alert('Erreur lors de la génération du PDF');
    }
  }

  async function sauvegarderSoumission() {
    try {
      setSaving(true);
      const payload = { ...formSoum };
      if (!acompteOverride) payload.acompte_montant = null;
      const data = await api.put(`/corpo/contrats/${id}`, payload);
      setContrat(data);
      showMsg('Soumission sauvegardée');
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function envoyerContrat() {
    if (!window.confirm('Envoyer ce contrat au client par courriel ?')) return;
    try {
      setSaving(true);
      const data = await api.post(`/corpo/contrats/${id}/envoyer`);
      setContrat(data);
      showMsg('Contrat envoyé au client ✓');
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function dupliquerContrat() {
    try {
      setSaving(true);
      const nouveau = await api.post(`/corpo/contrats/${id}/dupliquer`);
      navigate(`/corpo/contrats/${nouveau.id}`);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function supprimerContrat() {
    if (!window.confirm(`Supprimer le contrat ${contrat.numero_contrat} définitivement ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/corpo/contrats/${id}`);
      navigate('/corpo');
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
    }
  }

  // ─── Items ──────────────────────────────────────────────────────────────────

  function ouvrirModalItem(item = null) {
    if (item) {
      setEditItem(item);
      setFormItem({
        categorie:    item.categorie,
        description:  item.description,
        detail:       item.detail || '',
        quantite:     item.quantite,
        unite:        item.unite,
        prix_unitaire:item.prix_unitaire,
        est_rabais:   !!item.est_rabais,
        visible_menu: !!item.visible_menu,
        ordre:        item.ordre,
      });
    } else {
      setEditItem(null);
      setFormItem({
        categorie: '', description: '', detail: '', quantite: 1,
        unite: 'personne', prix_unitaire: '', est_rabais: false, visible_menu: true, ordre: 0,
      });
    }
    setShowItemModal(true);
  }

  async function sauvegarderItem(e) {
    e.preventDefault();
    try {
      setSaving(true);
      if (editItem) {
        await api.put(`/corpo/items/${editItem.id}`, formItem);
      } else {
        await api.post(`/corpo/contrats/${id}/items`, formItem);
      }
      await charger();
      setShowItemModal(false);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function supprimerItem(itemId) {
    if (!window.confirm('Supprimer cet item ?')) return;
    try {
      await api.delete(`/corpo/items/${itemId}`);
      await charger();
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
  }

  // ─── Commentaires ───────────────────────────────────────────────────────────

  async function envoyerCommentaire(e) {
    e.preventDefault();
    if (!nouveauMsg.trim()) return;
    try {
      await api.post(`/corpo/contrats/${id}/commentaires`, { message: nouveauMsg });
      setNouveauMsg('');
      await charger();
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
  }

  // ─── Appliquer menu préenregistré (prix_catalogue) ─────────────────────────

  async function appliquerMenuCatalogue(menuId) {
    setShowMenuCatDropdown(false);
    setAppliqueMenuLoading(true);
    try {
      const res = await api.post(`/menus-corpo/${menuId}/appliquer-contrat`, {
        contrat_id:  contrat.id,
        nb_personnes: contrat.nb_personnes || 50,
      });
      setMsg(`Menu appliqué — ${res.items_crees?.length || 0} item(s) ajouté(s) ✓`);
      setTimeout(() => setMsg(''), 5000);
      await charger();
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setAppliqueMenuLoading(false);
    }
  }

  // ─── Fonctions menus ────────────────────────────────────────────────────────

  async function chargerMenus() {
    try {
      setLoadingMenus(true);
      const data = await api.get(`/corpo/contrats/${id}/menus`);
      setMenus(data || []);
    } catch { setMenus([]); }
    finally { setLoadingMenus(false); }
  }

  async function chargerSectionsMenu(menuId) {
    try {
      const data = await api.get(`/corpo/menus/${menuId}/sections`);
      setMenuSections(data || []);
    } catch { setMenuSections([]); }
  }

  async function ouvrirMenu(menu) {
    setMenuActif(menu);
    await chargerSectionsMenu(menu.id);
    if (!catalogue.length) {
      try { const cats = await api.get('/catalogue?actif=1'); setCatalogue(cats || []); } catch {}
    }
  }

  async function sauvegarderMenu(e) {
    e.preventDefault();
    try {
      setSaving(true);
      let result;
      if (editMenu) {
        result = await api.put(`/corpo/menus/${editMenu.id}`, formMenu);
      } else {
        result = await api.post(`/corpo/contrats/${id}/menus`, formMenu);
      }
      // Avertissement si le prix change
      if (result.warning) {
        setConfirmPrixModal({ ...result, menuId: editMenu.id });
        return;
      }
      if (result.contrat) setContrat(result.contrat);
      await chargerMenus();
      setShowMenuModal(false);
      showMsg(editMenu ? 'Menu mis à jour — Soumission recalculée ✓' : 'Menu créé ✓');
    } catch (err) {
      // 409 spécial : acompte payé, l'admin doit confirmer l'écrasement
      if (err.response?.status === 409 && err.response?.data?.acompte_paye) {
        const msg = err.response.data.error + '\n\nContinuer quand même ? (le client a déjà payé son acompte)';
        if (window.confirm(msg)) {
          try {
            const forced = await api.put(`/corpo/menus/${editMenu.id}`, { ...formMenu, force_apres_acompte: true });
            if (forced.warning) {
              setConfirmPrixModal({ ...forced, menuId: editMenu.id });
              return;
            }
            if (forced.contrat) setContrat(forced.contrat);
            await chargerMenus();
            setShowMenuModal(false);
            showMsg('Menu mis à jour malgré acompte payé ✓');
          } catch (e2) { alert('Erreur : ' + (e2.response?.data?.error || e2.message)); }
        }
        return;
      }
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    finally { setSaving(false); }
  }

  async function confirmerMajPrix() {
    try {
      setSaving(true);
      const result = await api.put(`/corpo/menus/${confirmPrixModal.menuId}`, { ...formMenu, confirm: true });
      if (result.contrat) setContrat(result.contrat);
      await chargerMenus();
      setShowMenuModal(false);
      setConfirmPrixModal(null);
      showMsg('Menu mis à jour — Soumission recalculée ✓');
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.acompte_paye) {
        const msg = err.response.data.error + '\n\nContinuer quand même ? (le client a déjà payé son acompte)';
        if (window.confirm(msg)) {
          try {
            const forced = await api.put(`/corpo/menus/${confirmPrixModal.menuId}`, { ...formMenu, confirm: true, force_apres_acompte: true });
            if (forced.contrat) setContrat(forced.contrat);
            await chargerMenus();
            setShowMenuModal(false);
            setConfirmPrixModal(null);
            showMsg('Menu mis à jour malgré acompte payé ✓');
          } catch (e2) { alert('Erreur : ' + (e2.response?.data?.error || e2.message)); }
        }
        return;
      }
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    finally { setSaving(false); }
  }

  async function supprimerMenu(menuId) {
    if (!window.confirm('Supprimer ce menu et toutes ses sections ?')) return;
    try {
      const result = await api.delete(`/corpo/menus/${menuId}`);
      if (result.contrat) setContrat(result.contrat);
      if (menuActif?.id === menuId) { setMenuActif(null); setMenuSections([]); }
      await chargerMenus();
      showMsg('Menu supprimé ✓');
    } catch (err) { alert('Erreur : ' + err.message); }
  }

  async function toggleMenuDetail(itemId) {
    const newSet = new Set(expandedMenuItems);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
      setExpandedMenuItems(newSet);
      return;
    }
    newSet.add(itemId);
    setExpandedMenuItems(newSet);
    if (!menuDetailsCache[itemId]) {
      const menuObj = menus.find(m => m.item_soumission_id === itemId);
      if (menuObj) {
        try {
          const sections = await api.get(`/corpo/menus/${menuObj.id}/sections`);
          setMenuDetailsCache(c => ({ ...c, [itemId]: sections || [] }));
        } catch { setMenuDetailsCache(c => ({ ...c, [itemId]: [] })); }
      } else {
        setMenuDetailsCache(c => ({ ...c, [itemId]: [] }));
      }
    }
  }

  async function sauvegarderSection(e) {
    e.preventDefault();
    try {
      setSaving(true);
      if (editSection) {
        await api.put(`/corpo/sections/${editSection.id}`, formSection);
      } else {
        await api.post(`/corpo/menus/${menuActif.id}/sections`, formSection);
      }
      await chargerSectionsMenu(menuActif.id);
      setShowSectionModal(false);
    } catch (err) { alert('Erreur : ' + err.message); }
    finally { setSaving(false); }
  }

  async function supprimerSection(sectionId) {
    if (!window.confirm('Supprimer cette section et tous ses items ?')) return;
    try {
      await api.delete(`/corpo/sections/${sectionId}`);
      await chargerSectionsMenu(menuActif.id);
    } catch (err) { alert('Erreur : ' + err.message); }
  }

  async function ajouterItemDepuisCatalogue(produit) {
    try {
      await api.post(`/corpo/sections/${sectionCible.id}/items`, {
        catalogue_id: produit.id,
        nom_override: '',
        quantite: 1,
        unite: produit.unite_prix || 'portion',
        quantite_par_personne: null,
        prix_unitaire: produit.prix_base || 0,
        est_choix: 0,
        ordre: 0,
      });
      await chargerSectionsMenu(menuActif.id);
      setShowCatModal(false);
    } catch (err) { alert('Erreur : ' + err.message); }
  }

  async function supprimerItemMenu(itemId) {
    try {
      await api.delete(`/corpo/items-menu/${itemId}`);
      await chargerSectionsMenu(menuActif.id);
    } catch (err) { alert('Erreur : ' + err.message); }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function showMsg(m) {
    setMsg(m);
    setTimeout(() => setMsg(''), 3000);
  }

  function copierLienClient() {
    const lien = `${PUBLIC_FORM_URL}/contrat/${contrat.token_acces}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(lien).then(() => showMsg('Lien copié ✓'));
    } else {
      // Fallback pour HTTP non-sécurisé (accès réseau local)
      const el = document.createElement('textarea');
      el.value = lien;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showMsg('Lien copié ✓');
    }
  }

  if (loading) return (
    <div className="contrat-page">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
          <div className="skeleton" style={{ height: 32, width: 90, borderRadius: 'var(--radius-md)' }} />
          <div className="skeleton" style={{ height: 32, width: 220 }} />
          <div className="skeleton" style={{ height: 24, width: 80, borderRadius: 999 }} />
        </div>
        <div className="skeleton" style={{ height: 44, width: '100%', borderRadius: 'var(--radius-md)' }} />
        <div className="card section-card">
          {[200, 160, 140, 100].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: 14, width: w, marginBottom: 12 }} />
          ))}
        </div>
      </div>
    </div>
  );
  if (erreur) return (
    <div className="contrat-page">
      <div className="error-inline" style={{ marginTop: 'var(--space-7)' }}>
        <Icon name="erreur" size={16} />
        {erreur}
      </div>
    </div>
  );
  if (!contrat) return null;

  const { client, items = [], commentaires = [] } = contrat;
  const nbNonLus  = commentaires.filter(c => c.auteur === 'client' && !c.lu_escamel).length;

  return (
    <div className="contrat-page">
      {/* Notification flash */}
      {msg && <div className="flash-msg">{msg}</div>}

      {/* En-tête */}
      <div className="contrat-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/corpo')}>
          <Icon name="retour" size={14} />
          Retour
        </button>
        <div className="contrat-title-block">
          <h1 className="page-title">{contrat.numero_contrat}</h1>
          <span className={BADGE_CLASSES[contrat.statut] || 'badge badge--muted'}>
            {STATUT_LABELS[contrat.statut] || contrat.statut}
          </span>
          {contrat.notes_internes?.includes('Gravity Forms') && (
            <span className="badge-gf">Via formulaire</span>
          )}
        </div>
        <div className="contrat-actions">
          {contrat.statut === 'brouillon' && (
            <button className="btn btn-primary" onClick={envoyerContrat} disabled={saving}>
              <Icon name="email" size={14} />
              Envoyer au client
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={() => window.open(`${PUBLIC_FORM_URL}/contrat/${contrat.token_acces}`, '_blank')}
            title="Aperçu espace client"
          >
            <Icon name="voir" size={14} />
            Aperçu client
          </button>
          <button className="btn btn-ghost" onClick={dupliquerContrat} disabled={saving} title="Dupliquer">
            <Icon name="document" size={14} />
          </button>
          <button className="btn btn-danger" onClick={supprimerContrat} title="Supprimer ce contrat">
            <Icon name="supprimer" size={14} />
          </button>
        </div>
      </div>

      <div className="contrat-meta muted">
        {client.entreprise} — {fmtDate(contrat.date_evenement)} — {contrat.nb_personnes} pers.
        {contrat.lieu && ` — ${contrat.lieu}`}
      </div>

      {/* Onglets */}
      <div className="onglets">
        {ONGLETS.map(o => (
          <button
            key={o}
            className={`onglet-btn ${onglet === o ? 'active' : ''}`}
            onClick={() => setOnglet(o)}
          >
            {o}
            {o === 'Commentaires' && nbNonLus > 0 && (
              <span className="badge-count">{nbNonLus}</span>
            )}
          </button>
        ))}
      </div>

      {/* ─── Onglet Soumission ─────────────────────────────────────────────── */}
      {onglet === 'Soumission' && (
        <div className="onglet-content">
          {contrat.notes_internes?.includes('Gravity Forms') && (() => {
            const ni = contrat.notes_internes || '';
            const fmatch = ni.match(/Fiabilité analyse:\s*(\d+)%/);
            const fiabilite = fmatch ? parseInt(fmatch[1]) : null;
            const cmatch = ni.match(/Champs manquants:\s*([^\n]+)/);
            const champsManquants = cmatch ? cmatch[1].trim() : null;
            const mmatch = ni.match(/Menu recommandé[^:]*:\s*([^\n]+)/i);
            const menuRec = mmatch ? mmatch[1].trim() : null;
            return (
              <div className="card section-card gf-lead-card">
                <div className="gf-lead-header">
                  <span className="badge-gf">Lead Gravity Forms</span>
                  {fiabilite !== null && (
                    <span className="muted" style={{ fontSize: 13 }}>
                      Fiabilité de l'analyse : <strong style={{ color: fiabilite >= 70 ? 'var(--success)' : fiabilite >= 40 ? 'var(--warning)' : 'var(--danger)' }}>{fiabilite}%</strong>
                    </span>
                  )}
                </div>
                {fiabilite !== null && (
                  <div className="gf-fiabilite">
                    <div className="gf-fiabilite-bar">
                      <div
                        className="gf-fiabilite-fill"
                        style={{
                          width: `${fiabilite}%`,
                          background: fiabilite >= 70 ? 'var(--success)' : fiabilite >= 40 ? 'var(--warning)' : 'var(--danger)',
                        }}
                      />
                    </div>
                  </div>
                )}
                {champsManquants && (
                  <div className="gf-manquants">
                    <Icon name="attention" size={14} />
                    Champs manquants : {champsManquants}
                  </div>
                )}
                {menuRec && (
                  <div className="gf-menu-recommande">
                    Menu recommandé par Claude : <strong>{menuRec}</strong>
                  </div>
                )}
                <div className="gf-lead-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={envoyerContrat}
                    disabled={saving || !['en_construction', 'brouillon'].includes(contrat.statut)}
                  >
                    Envoyer la soumission au client
                  </button>
                </div>
              </div>
            );
          })()}
          <div className="soumission-grid">
            {/* Colonne gauche */}
            <div className="soumission-left">
              {/* Détails événement */}
              <div className="card section-card">
                <h3 className="section-title">Détails de l'événement</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date *</label>
                    <DateInput value={formSoum.date_evenement || ''}
                      onChange={v => setFormSoum(f => ({ ...f, date_evenement: v }))} />
                  </div>
                  <div className="form-group">
                    <label>Nb personnes</label>
                    <input type="number" min="1" value={formSoum.nb_personnes || ''}
                      onChange={e => setFormSoum(f => ({ ...f, nb_personnes: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Lieu</label>
                  <input type="text" value={formSoum.lieu || ''}
                    onChange={e => setFormSoum(f => ({ ...f, lieu: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input type="text" value={formSoum.adresse || ''}
                    onChange={e => setFormSoum(f => ({ ...f, adresse: e.target.value }))} />
                </div>
                <div className="form-row-3">
                  <div className="form-group">
                    <label>Arrivée</label>
                    <input type="time" value={formSoum.heure_arrivee || ''}
                      onChange={e => setFormSoum(f => ({ ...f, heure_arrivee: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Service</label>
                    <input type="time" value={formSoum.heure_service || ''}
                      onChange={e => setFormSoum(f => ({ ...f, heure_service: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Départ</label>
                    <input type="time" value={formSoum.heure_depart || ''}
                      onChange={e => setFormSoum(f => ({ ...f, heure_depart: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="card section-card">
                <div className="section-header">
                  <h3 className="section-title">Items de la soumission</h3>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
                    {/* Dropdown menus préenregistrés */}
                    <div style={{ position: 'relative' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowMenuCatDropdown(v => !v)}
                        disabled={appliqueMenuLoading}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {appliqueMenuLoading ? '…' : '+ Depuis menu ▾'}
                      </button>
                      {showMenuCatDropdown && (
                        <div style={{
                          position: 'absolute', top: '100%', right: 0, zIndex: 200,
                          background: 'var(--card)', border: '1px solid var(--border)',
                          borderRadius: 8, minWidth: 240, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                          overflow: 'hidden', marginTop: 4,
                        }}
                          onMouseLeave={() => setShowMenuCatDropdown(false)}
                        >
                          {menusCatalogue.length === 0 ? (
                            <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--muted)' }}>
                              Aucun menu disponible
                            </div>
                          ) : (
                            menusCatalogue.map(m => (
                              <button
                                key={m.id}
                                className="btn btn-ghost"
                                style={{
                                  width: '100%', textAlign: 'left', borderRadius: 0,
                                  padding: '8px 14px', fontSize: 13,
                                  display: 'flex', alignItems: 'center', gap: 8,
                                }}
                                onClick={() => appliquerMenuCatalogue(m.id)}
                              >
                                <span>{m.emoji}</span>
                                <span>{m.nom}</span>
                              </button>
                            ))
                          )}
                          <div style={{ borderTop: '1px solid var(--border)', padding: '6px 8px' }}>
                            <a href="/corpo/prix-catalogue" style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>
                              + Gérer les menus →
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => ouvrirModalItem()}>+ Item manuel</button>
                  </div>
                </div>
                {items.length === 0 ? (
                  <p className="muted" style={{ fontSize: 13 }}>Aucun item. Ajoutez des produits ou services.</p>
                ) : (
                  <table className="esc-table items-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qté</th>
                        <th>Prix unit.</th>
                        <th>Sous-total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => {
                        const isMenu    = item.categorie === 'menu';
                        const isExpanded = expandedMenuItems.has(item.id);
                        const menuObj   = isMenu ? menus.find(m => m.item_soumission_id === item.id) : null;
                        return (
                          <React.Fragment key={item.id}>
                            <tr className={`${item.est_rabais ? 'item-rabais' : ''} ${isMenu ? 'item-menu-row' : ''}`}>
                              <td>
                                <div className="item-desc">
                                  {isMenu
                                    ? <span className="item-cat-menu-badge">MENU</span>
                                    : <span className="item-cat muted">{item.categorie}</span>
                                  }
                                  <span>{!!item.est_rabais && '- '}{item.description}</span>
                                  {item.detail && <span className="item-detail muted">{item.detail}</span>}
                                </div>
                              </td>
                              <td className="centered">{item.quantite} {item.unite}</td>
                              <td className="montant">{fmtMontant(item.prix_unitaire)}</td>
                              <td className={`montant ${item.est_rabais ? 'danger' : ''}`}>
                                {item.est_rabais ? '−' : ''}{fmtMontant(item.quantite * item.prix_unitaire)}
                              </td>
                              <td className="item-actions">
                                {isMenu ? (
                                  <>
                                    <button className="btn btn-ghost btn-xs" title="Voir le détail" onClick={() => toggleMenuDetail(item.id)}>
                                      <Icon name={isExpanded ? 'attention' : 'chevron'} size={12} />
                                    </button>
                                    {menuObj && (
                                      <button className="btn btn-ghost btn-xs" title="Éditer le menu" onClick={() => {
                                        setOnglet('Menu');
                                        setTimeout(() => ouvrirMenu(menuObj), 150);
                                      }}>
                                        <Icon name="modifier" size={12} />
                                      </button>
                                    )}
                                    <button className="btn btn-danger btn-xs" onClick={() => menuObj && supprimerMenu(menuObj.id)}>
                                      <Icon name="fermer" size={12} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button className="btn btn-ghost btn-xs" onClick={() => ouvrirModalItem(item)}>
                                      <Icon name="modifier" size={12} />
                                    </button>
                                    <button className="btn btn-danger btn-xs" onClick={() => supprimerItem(item.id)}>
                                      <Icon name="fermer" size={12} />
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                            {isMenu && isExpanded && (
                              <tr className="item-menu-detail-row">
                                <td colSpan={5} style={{ padding: 0 }}>
                                  <div className="item-menu-detail">
                                    {!menuDetailsCache[item.id] ? (
                                      <span className="muted" style={{ fontSize:12 }}>Chargement…</span>
                                    ) : menuDetailsCache[item.id].length === 0 ? (
                                      <span className="muted" style={{ fontSize:12 }}>Aucun contenu défini dans ce menu</span>
                                    ) : (
                                      menuDetailsCache[item.id].map(sec => (
                                        <div key={sec.id} style={{ marginBottom:8 }}>
                                          <strong style={{ fontSize:12 }}>{sec.emoji} {sec.nom}</strong>
                                          <div style={{ marginLeft:14 }}>
                                            {(sec.items || []).map(it => (
                                              <div key={it.id} style={{ fontSize:12, color:'var(--muted)', lineHeight:'1.6' }}>
                                                • {it.nom_override || it.catalogue?.nom || '—'}
                                                {it.quantite_par_personne != null && ` (${it.quantite_par_personne} ${it.unite}/pers.)`}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Notes */}
              <div className="card section-card">
                <h3 className="section-title">Notes</h3>
                <div className="form-group">
                  <label>Notes pour le client</label>
                  <textarea rows={3} value={formSoum.notes_client || ''}
                    onChange={e => setFormSoum(f => ({ ...f, notes_client: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Notes internes (non visible par le client)</label>
                  <textarea rows={3} value={formSoum.notes_internes || ''}
                    onChange={e => setFormSoum(f => ({ ...f, notes_internes: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Colonne droite — résumé financier */}
            <div className="soumission-right">
              <div className="card section-card resume-financier">
                <h3 className="section-title">Résumé financier</h3>

                <div className="resume-ligne">
                  <span className="muted">Sous-total</span>
                  <span>{fmtMontant(contrat.sous_total)}</span>
                </div>
                <div className="resume-ligne">
                  <span className="muted">TPS ({contrat.tps_pct}%)</span>
                  <span>{fmtMontant(contrat.tps_montant)}</span>
                </div>
                <div className="resume-ligne">
                  <span className="muted">TVQ ({contrat.tvq_pct}%)</span>
                  <span>{fmtMontant(contrat.tvq_montant)}</span>
                </div>
                <div className="resume-ligne total">
                  <span>TOTAL TTC</span>
                  <span className="gold">{fmtMontant(contrat.total_ttc)}</span>
                </div>

                <hr className="resume-hr" />

                {/* Acompte */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Acompte (%)</label>
                    <input type="number" min="0" max="100" step="5"
                      value={formSoum.acompte_pct || ''}
                      onChange={e => setFormSoum(f => ({ ...f, acompte_pct: e.target.value }))}
                      disabled={acompteOverride}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={acompteOverride}
                        onChange={e => setAcompteOverride(e.target.checked)} />
                      Montant fixe
                    </label>
                    <input type="number" min="0" step="0.01"
                      value={formSoum.acompte_montant || ''}
                      onChange={e => setFormSoum(f => ({ ...f, acompte_montant: e.target.value }))}
                      disabled={!acompteOverride}
                      placeholder="Montant $"
                    />
                  </div>
                </div>

                <div className="resume-ligne">
                  <span className={`muted ${contrat.acompte_paye ? 'paid' : ''}`}>
                    Acompte {contrat.acompte_paye && <Icon name="check" size={12} style={{ marginLeft: 4 }} />}
                    {!contrat.acompte_paye && ' (dû)'}
                  </span>
                  <span className={contrat.acompte_paye ? 'paid' : ''}>
                    {fmtMontant(contrat.acompte_montant)}
                  </span>
                </div>
                <div className="resume-ligne">
                  <span className={`muted ${contrat.solde_paye ? 'paid' : ''}`}>
                    Solde {contrat.solde_paye && <Icon name="check" size={12} style={{ marginLeft: 4 }} />}
                    {!contrat.solde_paye && ' (dû)'}
                  </span>
                  <span className={contrat.solde_paye ? 'paid' : ''}>
                    {fmtMontant(contrat.solde_montant)}
                  </span>
                </div>
                {contrat.solde_due_date && (
                  <div className="resume-ligne">
                    <span className="muted">Solde dû le</span>
                    <span>{fmtDate(contrat.solde_due_date)}</span>
                  </div>
                )}

                <hr className="resume-hr" />

                {/* Taxes */}
                <div className="form-row">
                  <div className="form-group">
                    <label>TPS %</label>
                    <input type="number" min="0" step="0.001"
                      value={formSoum.tps_pct || ''}
                      onChange={e => setFormSoum(f => ({ ...f, tps_pct: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>TVQ %</label>
                    <input type="number" min="0" step="0.001"
                      value={formSoum.tvq_pct || ''}
                      onChange={e => setFormSoum(f => ({ ...f, tvq_pct: e.target.value }))} />
                  </div>
                </div>

                <div className="exemptions-row">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!client.tps_exempt}
                      onChange={async e => {
                        await api.put(`/corpo/clients/${client.id}`, { ...client, tps_exempt: e.target.checked ? 1 : 0 });
                        await charger();
                      }}
                    /> Exempt TPS
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!client.tvq_exempt}
                      onChange={async e => {
                        await api.put(`/corpo/clients/${client.id}`, { ...client, tvq_exempt: e.target.checked ? 1 : 0 });
                        await charger();
                      }}
                    /> Exempt TVQ
                  </label>
                </div>

                <button className="btn btn-primary full-width" onClick={sauvegarderSoumission} disabled={saving}>
                  {saving ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Onglet Menu ──────────────────────────────────────────────────── */}
      {onglet === 'Menu' && (
        <div className="onglet-content">
          {/* Vue liste des menus */}
          {!menuActif ? (
            <div className="card section-card">
              <div className="section-header">
                <h3 className="section-title">Menus de l'événement</h3>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setEditMenu(null);
                  setFormMenu({ nom: 'Menu principal', format: 'plateau', description: '', nb_personnes: '', statut: 'brouillon', notes: '', prix_par_personne: 0, nb_personnes_override: '', detail_visible: 0 });
                  setShowMenuModal(true);
                }}>+ Créer un menu</button>
              </div>
              {loadingMenus && <p className="muted">Chargement…</p>}
              {!loadingMenus && menus.length === 0 && (
                <p className="muted" style={{ fontSize: 13 }}>Aucun menu créé. Créez un menu pour définir ce que le client mangera.</p>
              )}
              {menus.map(m => {
                const nbPers = m.nb_personnes_override || contrat.nb_personnes || 0;
                const totalMenu = (m.prix_par_personne || 0) * nbPers;
                return (
                  <div key={m.id} className="menu-card-item" style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                      <Icon name="repas" size={20} />
                    </span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600 }}>{m.nom}</div>
                      <div className="muted" style={{ fontSize:12 }}>
                        {m.format === 'plateau' ? 'Plateau' : m.format === 'sandwich' ? 'Sandwich' : 'Libre'}
                        {' · '}{nbPers} pers.
                        {m.prix_par_personne > 0 && (
                          <span style={{ color:'var(--accent)', fontWeight:600, marginLeft:6 }}>
                            {m.prix_par_personne}$/pers · Total : {fmtMontant(totalMenu)}
                          </span>
                        )}
                        {' · '}<span className={m.statut === 'confirme' ? 'success' : 'muted'}>
                          {m.statut === 'confirme' ? 'Confirmé' : m.statut === 'propose' ? 'Proposé' : 'Brouillon'}
                        </span>
                      </div>
                      {m.prix_par_personne > 0 && m.item_soumission_id ? (
                        <div style={{ fontSize:11, color:'var(--success)', marginTop:2, display:'flex', alignItems:'center', gap:4 }}>
                          <Icon name="check" size={11} /> Ajouté à la soumission
                        </div>
                      ) : m.prix_par_personne <= 0 ? (
                        <div style={{ fontSize:11, color:'var(--warning)', marginTop:2, display:'flex', alignItems:'center', gap:4 }}>
                          <Icon name="attention" size={11} /> Prix non défini — non ajouté à la soumission
                        </div>
                      ) : null}
                    </div>
                    <button className="btn btn-ghost btn-xs" title="Modifier infos du menu" onClick={() => {
                      setEditMenu(m);
                      setFormMenu({ nom: m.nom, format: m.format, description: m.description || '', nb_personnes: m.nb_personnes || '', statut: m.statut, notes: m.notes || '', prix_par_personne: m.prix_par_personne || 0, nb_personnes_override: m.nb_personnes_override || '', detail_visible: m.detail_visible || 0 });
                      setShowMenuModal(true);
                    }}>
                      <Icon name="modifier" size={13} />
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => ouvrirMenu(m)}>
                      <Icon name="voir" size={13} />
                      Sections
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => supprimerMenu(m.id)}>
                      <Icon name="fermer" size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Éditeur de menu actif */
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setMenuActif(null); setMenuSections([]); }}>
                  <Icon name="retour" size={13} />
                  Retour aux menus
                </button>
                <h3 style={{ margin:0 }}>{menuActif.nom}</h3>
                <span className="muted" style={{ fontSize:12 }}>{menuActif.nb_personnes || contrat.nb_personnes} personnes</span>
              </div>

              {/* Sections du menu */}
              <div className="card section-card">
                <div className="section-header">
                  <h3 className="section-title">Sections du menu</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    setEditSection(null);
                    setFormSection({ nom: '', type: 'fixe', emoji: '🍖', description: '' });
                    setShowSectionModal(true);
                  }}>+ Ajouter section</button>
                </div>

                {menuSections.length === 0 && (
                  <p className="muted" style={{ fontSize:13 }}>Aucune section. Ajoutez des sections (ex: Viandes, Accompagnements, Boissons).</p>
                )}

                {menuSections.map(sec => (
                  <div key={sec.id} className="menu-section-block" style={{ marginBottom:20, border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                      <span style={{ fontSize:18 }}>{sec.emoji || ''}</span>
                      <strong>{sec.nom}</strong>
                      <span className="muted" style={{ fontSize:11 }}>
                        {sec.type === 'choix' ? '(choix client)' : sec.type === 'quantite' ? '(quantités)' : '(fixe)'}
                      </span>
                      <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                        <button className="btn btn-ghost btn-xs" onClick={() => {
                          setEditSection(sec);
                          setFormSection({ nom: sec.nom, type: sec.type, emoji: sec.emoji || '', description: sec.description || '' });
                          setShowSectionModal(true);
                        }}>
                          <Icon name="modifier" size={12} />
                        </button>
                        <button className="btn btn-danger btn-xs" onClick={() => supprimerSection(sec.id)}>
                          <Icon name="fermer" size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Items de la section */}
                    {(sec.items || []).map(item => {
                      const nomAffiche = item.nom_override || item.catalogue?.nom || '—';
                      return (
                        <div key={item.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                          <span style={{ fontSize:16 }}>{item.catalogue?.emoji || ''}</span>
                          <div style={{ flex:1 }}>
                            <span>{nomAffiche}</span>
                            {item.quantite_par_personne && (
                              <span className="muted" style={{ fontSize:11, marginLeft:8 }}>{item.quantite_par_personne}{item.unite}/pers.</span>
                            )}
                          </div>
                          <button className="btn btn-danger btn-xs" onClick={() => supprimerItemMenu(item.id)}>
                            <Icon name="fermer" size={12} />
                          </button>
                        </div>
                      );
                    })}

                    <div style={{ marginTop:8, display:'flex', gap:8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setSectionCible(sec);
                        setCatSearch('');
                        setCatCateg('');
                        setShowCatModal(true);
                      }}>
                        <Icon name="ajouter" size={13} />
                        Depuis catalogue
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Onglet Client ────────────────────────────────────────────────── */}
      {onglet === 'Client' && (
        <div className="onglet-content">
          <div className="card section-card">
            <h3 className="section-title">Informations client</h3>
            <div className="client-details">
              <div className="detail-row">
                <span className="detail-label muted">Entreprise</span>
                <span className="detail-value">{client.entreprise}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label muted">Contact</span>
                <span className="detail-value">{client.contact_prenom} {client.contact_nom}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label muted">Courriel</span>
                <span className="detail-value">
                  <a href={`mailto:${client.contact_email}`} style={{ color: 'var(--gold-500)' }}>
                    {client.contact_email}
                  </a>
                </span>
              </div>
              {client.contact_tel && (
                <div className="detail-row">
                  <span className="detail-label muted">Téléphone</span>
                  <span className="detail-value">{client.contact_tel}</span>
                </div>
              )}
              {client.adresse_facturation && (
                <div className="detail-row">
                  <span className="detail-label muted">Adresse</span>
                  <span className="detail-value">{client.adresse_facturation}</span>
                </div>
              )}
              {(client.ville || client.code_postal) && (
                <div className="detail-row">
                  <span className="detail-label muted">Ville</span>
                  <span className="detail-value">{client.ville} {client.code_postal} {client.province}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label muted">TPS exempt</span>
                <span className={`detail-value ${client.tps_exempt ? 'success' : 'muted'}`}>
                  {client.tps_exempt ? 'Oui' : 'Non'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label muted">TVQ exempt</span>
                <span className={`detail-value ${client.tvq_exempt ? 'success' : 'muted'}`}>
                  {client.tvq_exempt ? 'Oui' : 'Non'}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/corpo/clients')}>
                Voir tous les clients →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Onglet Commentaires ──────────────────────────────────────────── */}
      {onglet === 'Commentaires' && (
        <div className="onglet-content">
          <div className="card section-card commentaires-section">
            <h3 className="section-title">Fil de discussion</h3>
            <div className="commentaires-liste">
              {commentaires.length === 0 && (
                <p className="muted" style={{ fontSize: 13 }}>Aucun commentaire.</p>
              )}
              {commentaires.map(c => (
                <div key={c.id} className={`commentaire ${c.auteur === 'client' ? 'cmt-client' : 'cmt-escamel'}`}>
                  <div className="cmt-header">
                    <span className="cmt-auteur">{c.auteur_nom || c.auteur}</span>
                    <span className="cmt-date muted">{fmtDatetime(c.cree_le)}</span>
                  </div>
                  <div className="cmt-message">{c.message}</div>
                </div>
              ))}
            </div>
            <form onSubmit={envoyerCommentaire} className="commentaire-form">
              <textarea
                rows={3}
                placeholder="Votre message…"
                value={nouveauMsg}
                onChange={e => setNouveauMsg(e.target.value)}
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={!nouveauMsg.trim()}>
                Envoyer
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ─── Onglet Paiements ─────────────────────────────────────────────── */}
      {onglet === 'Paiements' && (
        <div className="onglet-content">
          <div className="card section-card">
            <h3 className="section-title">État des paiements</h3>
            <div className="paiements-grid">
              <div className={`paiement-card ${contrat.acompte_paye ? 'paye' : 'non-paye'}`}>
                <div className="paiement-label">Acompte ({contrat.acompte_pct}%)</div>
                <div className="paiement-montant">{fmtMontant(contrat.acompte_montant)}</div>
                <div className={`paiement-statut ${contrat.acompte_paye ? 'success' : 'warning'}`}>
                  {contrat.acompte_paye ? `Payé le ${fmtDate(contrat.acompte_date)}` : 'En attente'}
                </div>
              </div>
              <div className={`paiement-card ${contrat.solde_paye ? 'paye' : 'non-paye'}`}>
                <div className="paiement-label">Solde</div>
                <div className="paiement-montant">{fmtMontant(contrat.solde_montant)}</div>
                <div className={`paiement-statut ${contrat.solde_paye ? 'success' : 'warning'}`}>
                  {contrat.solde_paye ? `Payé le ${fmtDate(contrat.solde_date)}` : `Dû le ${fmtDate(contrat.solde_due_date)}`}
                </div>
              </div>
            </div>
            <hr className="resume-hr" style={{ margin: '20px 0' }} />
            <div className="paiement-actions">
              <div>
                <p className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                  Partagez le lien de paiement en ligne avec le client :
                </p>
                <button className="btn btn-ghost" onClick={copierLienClient}>
                  <Icon name="lien" size={14} />
                  Copier le lien client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Onglet Documents ─────────────────────────────────────────────── */}
      {onglet === 'Documents' && (
        <div className="onglet-content">
          <div className="card section-card">
            <h3 className="section-title">Documents</h3>
            <div className="documents-grid">
              <button
                className="doc-card"
                onClick={() => ouvrirPdf(`/api/corpo/contrats/${id}/pdf-facture`)}
              >
                <div className="doc-icon"><Icon name="facture" size={32} /></div>
                <div className="doc-name">Soumission / Facture PDF</div>
                <div className="doc-action muted">Télécharger</div>
              </button>
              <button
                className="doc-card"
                onClick={() => ouvrirPdf(`/api/corpo/contrats/${id}/pdf-menu`)}
              >
                <div className="doc-icon"><Icon name="repas" size={32} /></div>
                <div className="doc-name">Menu BBQ PDF</div>
                <div className="doc-action muted">Télécharger</div>
              </button>
            </div>
            <hr className="resume-hr" style={{ margin: '20px 0' }} />
            <div>
              <h4 style={{ marginBottom: 10, fontSize: 14, color: 'var(--muted)' }}>Lien client</h4>
              <div className="lien-client-box">
                <code className="lien-client-url">
                  {PUBLIC_FORM_URL}/contrat/{contrat.token_acces}
                </code>
                <button className="btn btn-ghost btn-sm" onClick={copierLienClient}>
                  Copier
                </button>
              </div>
              {contrat.statut === 'accepte' && contrat.signe_le && (
                <div style={{ marginTop: 16, padding: 12, background: 'color-mix(in srgb, var(--success) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name="succes" size={16} style={{ color: 'var(--success)' }} />
                  <span className="success">Signé par {contrat.signe_par_nom} le {fmtDatetime(contrat.signe_le)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Item ───────────────────────────────────────────────────── */}
      {showItemModal && (
        <div className="modal-overlay" onClick={() => setShowItemModal(false)}>
          <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editItem ? 'Modifier l\'item' : 'Ajouter un item'}</h3>
              <button className="modal-close" onClick={() => setShowItemModal(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <form onSubmit={sauvegarderItem} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie *</label>
                  <input type="text" value={formItem.categorie}
                    onChange={e => setFormItem(f => ({ ...f, categorie: e.target.value }))}
                    placeholder="Ex: Service, Viande, Accompagnements…"
                    required />
                </div>
                <div className="form-group">
                  <label>Unité</label>
                  <input type="text" value={formItem.unite}
                    onChange={e => setFormItem(f => ({ ...f, unite: e.target.value }))}
                    placeholder="personne, kg, forfait…" />
                </div>
              </div>
              <div className="form-group">
                <label>Description *</label>
                <input type="text" value={formItem.description}
                  onChange={e => setFormItem(f => ({ ...f, description: e.target.value }))}
                  required />
              </div>
              <div className="form-group">
                <label>Détail (optionnel)</label>
                <input type="text" value={formItem.detail}
                  onChange={e => setFormItem(f => ({ ...f, detail: e.target.value }))}
                  placeholder="Description additionnelle…" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantité</label>
                  <input type="number" min="0" step="0.01" value={formItem.quantite}
                    onChange={e => setFormItem(f => ({ ...f, quantite: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Prix unitaire ($) *</label>
                  <input type="number" min="0" step="0.01" value={formItem.prix_unitaire}
                    onChange={e => setFormItem(f => ({ ...f, prix_unitaire: e.target.value }))}
                    required />
                </div>
                <div className="form-group">
                  <label>Ordre</label>
                  <input type="number" value={formItem.ordre}
                    onChange={e => setFormItem(f => ({ ...f, ordre: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formItem.est_rabais}
                    onChange={e => setFormItem(f => ({ ...f, est_rabais: e.target.checked }))} />
                  C'est un rabais / crédit
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={formItem.visible_menu}
                    onChange={e => setFormItem(f => ({ ...f, visible_menu: e.target.checked }))} />
                  Visible dans le menu
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowItemModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sauvegarde…' : (editItem ? 'Modifier' : 'Ajouter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Créer/Modifier Menu ────────────────────────────────────── */}
      {showMenuModal && (
        <div className="modal-overlay" onClick={() => setShowMenuModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMenu ? 'Modifier le menu' : 'Créer un menu'}</h3>
              <button className="modal-close" onClick={() => setShowMenuModal(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <form onSubmit={sauvegarderMenu} className="modal-form">
              <div className="form-group">
                <label>Nom du menu *</label>
                <input type="text" value={formMenu.nom}
                  onChange={e => setFormMenu(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Ex: Plateau BBQ Texas" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Format</label>
                  <select value={formMenu.format} onChange={e => setFormMenu(f => ({ ...f, format: e.target.value }))}>
                    <option value="plateau">Plateau</option>
                    <option value="sandwich">Sandwich</option>
                    <option value="libre">Libre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Nb personnes (vide = contrat)</label>
                  <input type="number" min="1" value={formMenu.nb_personnes}
                    onChange={e => setFormMenu(f => ({ ...f, nb_personnes: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows={2} value={formMenu.description}
                  onChange={e => setFormMenu(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Statut</label>
                <select value={formMenu.statut} onChange={e => setFormMenu(f => ({ ...f, statut: e.target.value }))}>
                  <option value="brouillon">Brouillon</option>
                  <option value="propose">Proposé</option>
                  <option value="confirme">Confirmé</option>
                </select>
              </div>
              <hr style={{ margin: '16px 0', borderColor: 'var(--border)' }} />
              <h4 style={{ margin: '0 0 12px', color: 'var(--gold-500)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tarification</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Prix par personne ($/pers)</label>
                  <input type="number" min="0" step="0.01"
                    value={formMenu.prix_par_personne}
                    onChange={e => setFormMenu(f => ({ ...f, prix_par_personne: e.target.value }))}
                    placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>Nb personnes (vide = contrat)</label>
                  <input type="number" min="1"
                    value={formMenu.nb_personnes_override}
                    onChange={e => setFormMenu(f => ({ ...f, nb_personnes_override: e.target.value }))}
                    placeholder={`${contrat.nb_personnes} pers. (contrat)`} />
                </div>
              </div>
              {Number(formMenu.prix_par_personne) > 0 && (() => {
                const nb = Number(formMenu.nb_personnes_override) || contrat.nb_personnes || 0;
                const total = Number(formMenu.prix_par_personne) * nb;
                return (
                  <div style={{ background:'rgba(200,148,26,0.08)', border:'1px solid var(--gold-500)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                    <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Aperçu ligne soumission</div>
                    <div style={{ fontWeight:600, fontSize:13 }}>
                      {formMenu.nom || 'Menu'} — {nb} pers × {Number(formMenu.prix_par_personne).toFixed(2)}$/pers = <span style={{ color:'var(--gold-500)' }}>{fmtMontant(total)}</span>
                    </div>
                  </div>
                );
              })()}
              <div className="form-group">
                <label>Affichage dans la soumission</label>
                <select value={formMenu.detail_visible}
                  onChange={e => setFormMenu(f => ({ ...f, detail_visible: Number(e.target.value) }))}>
                  <option value={0}>Résumé seulement</option>
                  <option value={1}>Résumé + détail des items</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowMenuModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sauvegarde…' : (editMenu ? 'Sauvegarder et mettre à jour la soumission' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Confirmation prix changé ──────────────────────────────── */}
      {confirmPrixModal && (
        <div className="modal-overlay" onClick={() => setConfirmPrixModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>Mise à jour de la soumission</h3>
              <button className="modal-close" onClick={() => setConfirmPrixModal(null)}><Icon name="fermer" size={16} /></button>
            </div>
            <div style={{ padding: '0 4px 12px' }}>
              <p style={{ marginBottom: 14, fontSize: 14 }}>{confirmPrixModal.message}</p>
              <div style={{ background:'var(--surface)', borderRadius:8, padding:'12px 16px', display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                  <span className="muted">Ancien total menu</span>
                  <span>{fmtMontant(confirmPrixModal.ancien_total)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                  <span className="muted">Nouveau total menu</span>
                  <span className="success">{fmtMontant(confirmPrixModal.nouveau_total)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, borderTop:'1px solid var(--border)', paddingTop:8 }}>
                  <span className="muted">Différence</span>
                  <span className={confirmPrixModal.difference >= 0 ? 'success' : 'danger'}>
                    {confirmPrixModal.difference >= 0 ? '+' : ''}{fmtMontant(Math.abs(confirmPrixModal.difference))}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmPrixModal(null)}>Annuler</button>
              <button className="btn btn-primary" disabled={saving} onClick={confirmerMajPrix}>
                {saving ? 'Sauvegarde…' : 'Confirmer la mise à jour'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Créer/Modifier Section ────────────────────────────────── */}
      {showSectionModal && (
        <div className="modal-overlay" onClick={() => setShowSectionModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editSection ? 'Modifier la section' : 'Ajouter une section'}</h3>
              <button className="modal-close" onClick={() => setShowSectionModal(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <form onSubmit={sauvegarderSection} className="modal-form">
              <div className="form-row">
                <div className="form-group" style={{ flex: '0 0 60px' }}>
                  <label>Emoji</label>
                  <input type="text" value={formSection.emoji}
                    onChange={e => setFormSection(f => ({ ...f, emoji: e.target.value }))}
                    maxLength={2} style={{ textAlign: 'center', fontSize: 20 }} />
                </div>
                <div className="form-group">
                  <label>Nom de la section *</label>
                  <input type="text" value={formSection.nom}
                    onChange={e => setFormSection(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex: Viandes fumées, Accompagnements…" required />
                </div>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={formSection.type} onChange={e => setFormSection(f => ({ ...f, type: e.target.value }))}>
                  <option value="fixe">Fixe (servi à tous)</option>
                  <option value="choix">Choix (client choisit parmi les options)</option>
                  <option value="quantite">Quantité (client précise combien de chaque)</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowSectionModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sauvegarde…' : (editSection ? 'Modifier' : 'Ajouter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Catalogue Picker ───────────────────────────────────────── */}
      {showCatModal && (
        <div className="modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter depuis le catalogue</h3>
              <button className="modal-close" onClick={() => setShowCatModal(false)}><Icon name="fermer" size={16} /></button>
            </div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
              <input type="text" className="form-control" placeholder="Rechercher…"
                value={catSearch} onChange={e => setCatSearch(e.target.value)}
                style={{ flex: 1 }} />
              <select value={catCateg} onChange={e => setCatCateg(e.target.value)}>
                <option value="">Toutes catégories</option>
                <option value="viande">Viandes</option>
                <option value="sauce">Sauces</option>
                <option value="side">Sides</option>
                <option value="boisson">Boissons</option>
                <option value="extra">Extras</option>
              </select>
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {catalogue
                .filter(p => !catCateg || p.categorie === catCateg)
                .filter(p => !catSearch || p.nom.toLowerCase().includes(catSearch.toLowerCase()))
                .map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:22, minWidth:28 }}>{p.emoji || ''}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600 }}>{p.nom}</div>
                      {p.description_courte && <div className="muted" style={{ fontSize:12 }}>{p.description_courte}</div>}
                    </div>
                    {p.prix_base > 0 && <span className="muted" style={{ fontSize:12 }}>{p.prix_base}$/{p.unite_prix}</span>}
                    <button className="btn btn-primary btn-sm" onClick={() => ajouterItemDepuisCatalogue(p)}>
                      <Icon name="ajouter" size={13} />
                      Ajouter
                    </button>
                  </div>
                ))}
              {catalogue.filter(p => !catCateg || p.categorie === catCateg).filter(p => !catSearch || p.nom.toLowerCase().includes(catSearch.toLowerCase())).length === 0 && (
                <p className="muted" style={{ padding: 16, textAlign: 'center' }}>Aucun produit trouvé.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
